import React from 'react'
import { useMutation, gql } from '@apollo/client'
import { Row, Col, Input, Upload, Button, Image } from 'antd'
import {
  UploadOutlined,
  MinusCircleTwoTone,
  CaretUpFilled,
  CaretDownFilled,
} from '@ant-design/icons'
import * as _ from 'lodash'
import BlockElement from '../../../../components/BlockElement'
import { minusIconStyle, arrowIconStyle } from '../styles'
import { switchElements, disabledMove } from '../utils'

const UPLOAD_FICHE_IMAGE = gql`
  mutation uploadFicheImage($id: Int, $image: Upload!, $ficheComponentId: Int) {
    uploadFicheImage(
      id: $id
      image: $image
      ficheComponentId: $ficheComponentId
    ) {
      ficheImage {
        id
        url
      }
    }
  }
`

const REMOVE_FICHE_IMAGE = gql`
  mutation removeFicheImage($id: Int!) {
    removeFicheImage(id: $id) {
      success
    }
  }
`

interface Props {
  setReviews: (value: any) => void
  reviews: [any]
  index: number
}

const Review = (props: Props): JSX.Element => {
  const { TextArea } = Input
  const { index } = props

  const review = props.reviews[index]

  const [uploadFicheImage] = useMutation(UPLOAD_FICHE_IMAGE)
  const [removeImage] = useMutation(REMOVE_FICHE_IMAGE)

  const setReview = (values: any) => {
    const updatedReviews = [...props.reviews]
    const review = _.cloneDeep(updatedReviews[index])
    updatedReviews[index] = { ...review, ...values }

    props.setReviews(() => {
      return [...updatedReviews]
    })
  }

  const removeReview = () => {
    const newReviews = _.cloneDeep(props.reviews)
    newReviews.splice(index, 1)
    props.setReviews(newReviews)
  }

  const reorderReviews = (move: 'up' | 'down') => {
    const reviews = [...props.reviews]
    if (disabledMove(reviews, review, move)) return
    switchElements(reviews, review, move)
    props.setReviews(reviews)
  }

  const upload = (e: any): Promise<string | void | ArrayBuffer | null> => {
    const file = e.file
    return uploadFicheImage({
      variables: { image: file, ficheComponentId: props.reviews[index].id },
    }).then(
      ({
        data: {
          uploadFicheImage: { ficheImage },
        },
      }) => {
        setReview({
          image_id: ficheImage.id,
          image_url: ficheImage.url,
          image_name: file.name,
        })
      }
    )
  }

  const onRemoveImage = (file: any): Promise<void> => {
    return removeImage({
      variables: { id: file.id },
    }).then(() => {
      setReview({
        image_id: null,
        image_url: null,
        image_name: null,
      })
    })
  }

  const imageLoaded: any = {
    uid: 'none',
    status: 'done',
    name: review['image_name'],
    url: review['image_url'],
    id: review['image_id'],
  }

  const fileList = review['image_url'] ? [imageLoaded] : []

  return (
    <>
      <Row justify="end">
        <Col span={1}>
          <Row gutter={[6, 0]}>
            <Col>
              <Row>
                <CaretUpFilled
                  style={arrowIconStyle(
                    disabledMove(props.reviews, props.reviews[index], 'up')
                  )}
                  onMouseDown={() => reorderReviews('up')}
                />
              </Row>
              <Row>
                <CaretDownFilled
                  style={arrowIconStyle(
                    disabledMove(props.reviews, props.reviews[index], 'down')
                  )}
                  onMouseDown={() => reorderReviews('down')}
                />
              </Row>
            </Col>
            <Col>
              <MinusCircleTwoTone
                style={minusIconStyle}
                onMouseDown={() => removeReview()}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <BlockElement>
        <Row gutter={[5, 20]}>
          <TextArea
            autoSize={{ minRows: 3, maxRows: 3 }}
            onBlur={e => setReview({ content: e.target.value })}
            defaultValue={props.reviews?.[index]?.['content']}
          />
        </Row>
        <Row gutter={[5, 20]}>
          <Col span={4}>
            <Image width={200} src={review['image_url']} />
          </Col>
          <Col span={4}>
            <Upload
              customRequest={upload}
              onRemove={onRemoveImage}
              fileList={fileList}
            >
              {!review['image_url'] && (
                <Button icon={<UploadOutlined />}>Upload</Button>
              )}
            </Upload>
          </Col>
          <Col span={16}>
            <Row gutter={[5, 5]}>
              <label>alt text</label>
            </Row>
            <Row gutter={[5, 5]}>
              <Input
                onBlur={e => setReview({ alttext: e.target.value })}
                defaultValue={props.reviews?.[index]?.['alttext']}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[5, 20]}>
          <Col span={8}>
            <Row gutter={[5, 5]}>
              <label>Nom</label>
            </Row>
            <Row gutter={[5, 5]}>
              <Input
                style={{ width: '90%' }}
                onBlur={e => setReview({ name: e.target.value })}
                defaultValue={props.reviews?.[index]?.['name']}
              />
            </Row>
          </Col>
          <Col span={16}>
            <Row gutter={[5, 5]}>
              <label>Poste / Société</label>
            </Row>
            <Row gutter={[5, 5]}>
              <Input
                onBlur={e => setReview({ information: e.target.value })}
                defaultValue={props.reviews?.[index]?.['information']}
              />
            </Row>
          </Col>
        </Row>
      </BlockElement>
    </>
  )
}

export default Review
